import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ExternalLink, Truck, CheckCircle2, MessageSquareWarning, Undo2 } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import DocumentsPanel from "@/components/DocumentsPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CARD_IMPORT_ROLES } from "@/constants/roles";
import { qboTransactionLink } from "@/lib/qbo";
import { fmtDate, fmtDateTime } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function ContractorInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [codes, setCodes] = useState([]);
  const [po, setPo] = useState(null);
  const [edits, setEdits] = useState({}); // index -> {cost_code_id}
  const [showCorrection, setShowCorrection] = useState(false);
  const [correction, setCorrection] = useState("");
  const [busy, setBusy] = useState(false);
  const [qboEnvironment, setQboEnvironment] = useState(null);
  const { user } = useAuth();
  const canCredit = CARD_IMPORT_ROLES.includes(user?.role);
  const [syncingPayment, setSyncingPayment] = useState(false);
  // A payment recorded on a synced Bill waits for someone to push it
  // (Test Drive 2026-10-21 — no automatic pushes to QuickBooks).
  const syncPayment = async () => {
    setSyncingPayment(true);
    try {
      const { data } = await api.post(`/contractor/accounting/invoice/${id}/sync-payment`);
      if (data.ok) toast.success("Payment sent to QuickBooks");
      else toast.error(data.error || "QuickBooks payment sync failed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "QuickBooks payment sync failed");
    } finally {
      setSyncingPayment(false);
    }
  };

  const load = () => api.get(`/contractor/invoices/${id}`).then((r) => { setInv(r.data); setEdits({}); });
  useEffect(() => {
    load();
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    api.get("/contractor/contexts").then((r) => setContexts(r.data.filter((c) => c.active !== false))).catch(() => {});
    api.get("/contractor/cost-codes").then((r) => setCodes(r.data.filter((c) => c.active !== false)));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only fetched when there's actually something to link to — the
  // sandbox-vs-production QBO web host for building "View in QuickBooks"
  // links on the synced Bill/BillPayment below.
  useEffect(() => {
    if (qboEnvironment === null) {
      api.get("/contractor/accounting/qbo-environment").then((r) => setQboEnvironment(r.data.environment)).catch(() => {});
    }
  }, [qboEnvironment]);

  // The job isn't chosen on the invoice — it was pinned before shopping and
  // lives on the PO; every line inherits it (backend: approve_invoice).
  useEffect(() => {
    if (inv?.po_id) api.get(`/contractor/purchase-orders/${inv.po_id}`).then((r) => setPo(r.data)).catch(() => {});
  }, [inv?.po_id]);

  const reviewable = inv && (inv.status === "sent" || inv.status === "received");

  const lineValue = (idx, field) => {
    const e = edits[idx] || {};
    if (field in e) return e[field];
    return inv.lines[idx][field] || "";
  };

  const setEdit = (idx, field, value) => setEdits((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), [field]: value } }));

  const codedCount = useMemo(() => {
    if (!inv) return 0;
    return inv.lines.filter((_, idx) => lineValue(idx, "cost_code_id")).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv, edits]);

  if (!inv) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const buildReviewLines = () =>
    inv.lines
      .map((_, idx) => ({ index: idx, cost_code_id: lineValue(idx, "cost_code_id") || null }))
      .filter((e) => e.cost_code_id);

  const poContext = po?.context;
  const jobLabel = (() => {
    if (!poContext) return null;
    if (poContext.job_id) {
      const j = jobs.find((x) => x.id === poContext.job_id);
      return j ? `${j.code} — ${j.name}` : "Job";
    }
    const c = contexts.find((x) => x.id === poContext.context_id);
    return c ? c.name : "Non-job";
  })();

  const saveReview = async () => {
    const lines = buildReviewLines();
    if (!lines.length) return null;
    const r = await api.patch(`/contractor/invoices/${id}/review`, { lines });
    return r.data;
  };

  const approve = async () => {
    setBusy(true);
    try {
      await saveReview();
      const r = await api.post(`/contractor/invoices/${id}/approve`);
      setInv(r.data);
      setEdits({});
      toast.success(`Invoice ${r.data.invoice_number} approved — ready to sync to QuickBooks`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const sendCorrection = async () => {
    if (!correction.trim()) return toast.error("Describe what the vendor needs to fix");
    try {
      await api.post(`/contractor/invoices/${id}/request-correction`, { message: correction.trim() });
      toast.success("Correction requested — the invoice went back to the vendor");
      navigate("/contractor/accounting");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="contractor-invoice-detail-page">
      <Link to="/contractor/accounting" className="flex items-center gap-1 text-[13px] text-slate-500 hover:text-slate-800"><ChevronLeft size={15} /> Accounting</Link>
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-extrabold tracking-tight mono">{inv.invoice_number}</h1>
          <StatusBadge status={inv.status} />
          {/* accounting_status is a separate pipeline (pending_review -> ready_for_sync
              -> synced) from the invoice's own status (sent -> received -> approved ->
              synced -> paid) — worth a second badge when they diverge, but once a Bill
              is synced both read "synced" and showing it twice is just noise. */}
          {inv.accounting_status && inv.accounting_status !== inv.status && (
            <StatusBadge status={inv.accounting_status} testId="invoice-accounting-status" />
          )}
        </div>
        {reviewable && (
          <div className="flex gap-2">
            <button onClick={() => setShowCorrection(true)} data-testid="request-correction-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-amber-200 text-amber-700 text-[13px] font-semibold hover:bg-amber-50">
              <MessageSquareWarning size={14} /> Request correction
            </button>
            <button onClick={approve} disabled={busy} data-testid="approve-invoice-button" className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-500 text-white text-[13px] font-semibold hover:bg-emerald-600 disabled:opacity-50">
              <CheckCircle2 size={14} /> Approve ({codedCount}/{inv.lines.length} coded)
            </button>
          </div>
        )}
        {!reviewable && canCredit && ["approved", "synced", "paid"].includes(inv.status) && (
          <button onClick={() => navigate(`/contractor/accounting?tab=credits&credit_for=${inv.id}`)} data-testid="issue-vendor-credit-button"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-[13px] font-semibold hover:bg-slate-50">
            <Undo2 size={14} /> Issue vendor credit
          </button>
        )}
      </div>
      <div className="text-[13px] text-slate-500 mt-1">
        {inv.vendor_name} · PO{" "}
        <button onClick={() => navigate(`/contractor/purchase-orders/${inv.po_id}`)} className="mono text-sky-600 font-medium hover:underline">{inv.po_number}</button>
        {" "}· Due {fmtDate(inv.due_date)}
        {jobLabel && <>{" "}· <span data-testid="invoice-job">{poContext?.job_id ? "Job" : "Context"}: <b className="font-semibold text-slate-700">{jobLabel}</b></span></>}
      </div>

      {inv.budget_hold && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 px-3.5 py-2.5 text-[12.5px] text-amber-800" data-testid="budget-hold-banner">
          Waiting on budget — this invoice is more than the job has left. It isn't counted and can't be approved until the job's budget is raised
          {po?.job_id && <> (<Link to={`/contractor/jobs/${po.job_id}`} className="font-semibold underline">open the job</Link>)</>}.
        </div>
      )}
      {inv.status === "paid" && (
        <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-100 px-3.5 py-2.5 text-[12.5px] text-emerald-800" data-testid="paid-banner">
          Paid {inv.paid_at ? fmtDate(inv.paid_at) : ""} via {(inv.paid_method || "").replace(/_/g, " ")}
          {inv.paid_reference ? ` · ref ${inv.paid_reference}` : ""}
          {inv.qbo_payment_ref && (
            qboEnvironment ? (
              <>
                {" · "}
                <a href={qboTransactionLink(qboEnvironment, "BillPayment", inv.qbo_payment_ref)} target="_blank" rel="noopener noreferrer"
                  data-testid="view-payment-in-quickbooks-link" className="inline-flex items-center gap-1 text-emerald-900 font-medium hover:underline">
                  payment synced to QuickBooks <ExternalLink size={11} />
                </a>
              </>
            ) : " · payment synced to QuickBooks"
          )}
          {inv.qbo_payment_pending && (
            <>
              {" · "}payment not in QuickBooks yet
              {canCredit && (
                <button onClick={syncPayment} disabled={syncingPayment} data-testid="sync-payment-button" className="ml-2 px-2.5 py-1 rounded-md bg-[#2CA01C] hover:bg-[#248217] text-white text-[11.5px] font-semibold disabled:opacity-60">
                  {syncingPayment ? "Sending…" : "Send payment to QuickBooks"}
                </button>
              )}
            </>
          )}
        </div>
      )}
      {inv.qbo_sync && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-white border border-slate-100 px-3.5 py-2.5 text-[12.5px]" data-testid="qbo-sync-banner">
          <span className="text-slate-600">
            Synced to QuickBooks as {inv.qbo_sync.qbo_type} #{inv.qbo_sync.qbo_id}
            {inv.qbo_sync.at ? ` · ${fmtDateTime(inv.qbo_sync.at)}` : ""}
          </span>
          {qboEnvironment && (
            <a href={qboTransactionLink(qboEnvironment, inv.qbo_sync.qbo_type, inv.qbo_sync.qbo_id)} target="_blank" rel="noopener noreferrer"
              data-testid="view-invoice-in-quickbooks-link" className="flex items-center gap-1 text-sky-600 font-medium hover:underline shrink-0">
              View in QuickBooks <ExternalLink size={12} />
            </a>
          )}
        </div>
      )}
      {inv.sync_error && (
        <div className="mt-4 rounded-lg bg-rose-50 border border-rose-100 px-3.5 py-2.5 text-[12.5px] text-rose-800" data-testid="qbo-sync-error-banner">
          QuickBooks sync failed: {inv.sync_error.message}
        </div>
      )}
      {reviewable && (
        <div className="mt-4 rounded-lg bg-sky-50 border border-sky-100 px-3.5 py-2.5 text-[12.5px] text-sky-800" data-testid="review-explainer">
          Assign a <b>cost code to every line</b> before approving. The job comes from the purchase order{jobLabel ? <> (<b>{jobLabel}</b>)</> : ""}, so there's nothing to pick here. Lines start prefilled from the purchase order.
        </div>
      )}

      {inv.match && (
        <div className={`mt-4 rounded-lg border px-3.5 py-2.5 text-[12.5px] ${inv.match.status === "matched" ? "bg-emerald-50 border-emerald-100 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="match-panel">
          <div className="font-semibold">
            {inv.match.status === "matched"
              ? "✓ Three-way match — invoice agrees with the PO and receiving"
              : "Three-way match: discrepancies flagged (approval isn't blocked — accounting sees these)"}
          </div>
          {(inv.match.issues || []).map((iss, i) => (
            <div key={i} className={`mt-1 ${iss.severity === "info" ? "opacity-70" : ""}`} data-testid={`match-issue-${iss.type}`}>
              {iss.severity === "warning" ? "⚠ " : "ℹ︎ "}{iss.message}
            </div>
          ))}
        </div>
      )}

      {(inv.tracking_number || inv.delivery_date) && (
        <div className="mt-4 flex items-center gap-2 text-[12.5px] bg-white border border-slate-100 rounded-lg px-3.5 py-2.5" data-testid="invoice-tracking-banner">
          <Truck size={14} className="text-sky-500" />
          <span className="text-slate-700">
            {inv.carrier ? `${inv.carrier} · ` : ""}{inv.tracking_number ? `Tracking ${inv.tracking_number}` : ""}
            {inv.delivery_date ? ` · Delivery ${fmtDate(inv.delivery_date)}` : ""}
          </span>
        </div>
      )}

      <div className="mt-5 bg-white rounded-xl border border-slate-100 p-5">
        <h2 className="text-[15px] font-bold">Lines</h2>
        <div className="overflow-x-auto">
          <table className="w-full mt-3 text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                <th className="text-left py-2">Description</th><th className="text-right py-2 w-16">Qty</th><th className="text-right py-2 w-24">Amount</th>
                <th className="text-right py-2 w-28">Tax</th><th className="text-left py-2 pl-4 w-56">Cost Code</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l, idx) => (
                <tr key={idx} className="border-b border-slate-50" data-testid={`review-line-${idx}`}>
                  <td className="py-2.5 pr-2">{l.description}</td>
                  <td className="py-2.5 text-right">{l.quantity}</td>
                  <td className="py-2.5 text-right mono">{fmt(l.amount)}</td>
                  <td className="py-2.5 text-right mono text-slate-500" data-testid={`review-line-${idx}-tax`}>
                    {l.tax_amount ? <>{fmt(l.tax_amount)} <span className="text-[11px] text-slate-400">({Number(l.tax_rate)}%)</span></> : "—"}
                  </td>
                  <td className="py-2.5 pl-4">
                    {reviewable ? (
                      <SearchableSelect value={lineValue(idx, "cost_code_id")} onChange={(e) => setEdit(idx, "cost_code_id", e.target.value)} data-testid={`line-costcode-select-${idx}`}
                        className={`w-full h-9 px-2 rounded-lg border text-[12.5px] ${lineValue(idx, "cost_code_id") ? "border-slate-200" : "border-amber-300 bg-amber-50"}`}>
                        <option value="">— cost code —</option>
                        {codes.map((c) => <option key={c.id} value={c.id}>{c.code} {c.name}</option>)}
                      </SearchableSelect>
                    ) : (
                      <span className="text-slate-600 text-[12.5px]">{(codes.find((c) => c.id === l.cost_code_id) || {}).name || "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex justify-end text-[13px]">
          <div className="w-56 space-y-1.5">
            <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="mono">{fmt(inv.subtotal)}</span></div>
            <div className="flex justify-between text-slate-500"><span>Tax</span><span className="mono">{fmt(inv.tax_total)}</span></div>
            <div className="flex justify-between font-bold border-t border-slate-100 pt-1.5"><span>Total</span><span className="mono">{fmt(inv.total)}</span></div>
          </div>
        </div>
      </div>

      {inv.pay_link_url && (
        <div className="mt-4 bg-white rounded-xl border border-slate-100 p-4" data-testid="invoice-pay-link">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Vendor payment link</div>
          <a href={inv.pay_link_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-sky-600 text-[13px] font-medium hover:underline">
            {inv.pay_link_url} <ExternalLink size={12} />
          </a>
          <div className="mt-1 text-[11.5px] text-slate-400">Paying happens on the vendor's own payment provider — ScapeSource never handles funds.</div>
        </div>
      )}

      {inv.notes && (
        <div className="mt-4 bg-white rounded-xl border border-slate-100 p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Vendor notes</div>
          <div className="mt-1 text-slate-600 text-[13px]">{inv.notes}</div>
        </div>
      )}

      <div className="mt-5">
        <DocumentsPanel entityType="invoice" entityId={inv.id} />
      </div>

      <Dialog open={showCorrection} onOpenChange={setShowCorrection}>
        <DialogContent data-testid="correction-modal">
          <DialogHeader><DialogTitle>Request a correction from {inv.vendor_name}</DialogTitle></DialogHeader>
          <p className="text-[12.5px] text-slate-500">The invoice goes back to the vendor as a draft — they fix it and re-send.</p>
          <textarea value={correction} onChange={(e) => setCorrection(e.target.value)} rows={3} placeholder="What needs fixing? (quantities, pricing, missing freight line…)" data-testid="correction-message-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <button onClick={sendCorrection} data-testid="confirm-correction-button" className="w-full h-10 rounded-lg bg-amber-500 text-white text-[13px] font-semibold">Send back to vendor</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
